import React from "react";
import Modal from "../../../component/ui/modal";
import TextInput from "../../../component/element/forms/TextInput";
import TextBox from "../../../component/element/forms/TextBox";
import SelectOption from "../../../component/element/forms/SelectOption";
import Upload from "../../../component/modals/upload";
import DocumentUpload from "../../../component/modals/upload/DocumentUpload";
import { ISimpleData } from "../../../store/types/misc";
import { IMarketer } from "../../../store/types/marketers";
import useMarketerForm from "../hooks/useMarketerForm";

type IMarketerFormModal = {
  open: boolean;
  marketer?: IMarketer | null;
  onClose: () => void;
};

const verificationOptions: ISimpleData[] = [
  { id: "pending", name: "Pending" },
  { id: "verified", name: "Verified" },
  { id: "failed", name: "Failed" },
];

const statusOptions: ISimpleData[] = [
  { id: "pending", name: "Pending" },
  { id: "approved", name: "Approved" },
  { id: "rejected", name: "Rejected" },
];

const MarketerFormModal = ({
  open,
  marketer = null,
  onClose,
}: IMarketerFormModal) => {
  const {
    values,
    errors,
    companies,
    countries,
    photoPreview,
    cvPreview,
    loading,
    onChange,
    setFieldValue,
    onPhotoChange,
    onPhotoDelete,
    onCvChange,
    onCvDelete,
    submit,
  } = useMarketerForm(open, marketer);

  const handleSubmit = async () => {
    if (loading) return;
    const saved = await submit();
    if (saved) onClose();
  };

  const fieldStatus = (field: keyof typeof errors) =>
    errors[field] ? "error: this field is required" : "";
  const companyOptions: ISimpleData[] = companies.map((company) => ({
    id: company.id,
    name: company.company_name,
  }));
  const countryOptions: ISimpleData[] = countries.map((country) => ({
    id: country.id,
    name: country.name,
  }));
  const selectedCountryId =
    countries.find((country) => country.name === values.target_country)?.id ??
    "";

  return (
    <Modal
      title={marketer ? "Edit Marketer" : "Create Marketer"}
      open={open}
      onClose={onClose}
      onOkClick={handleSubmit}
      isLoading={loading}
      variant="large"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <TextInput
            label="Full name"
            name="name"
            type="text"
            placeholder="Enter full name"
            value={values.name}
            onChange={onChange}
            status={fieldStatus("name")}
            required
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Email"
            name="email"
            type="email"
            placeholder="name@example.com"
            value={values.email}
            onChange={onChange}
            status={fieldStatus("email")}
            required
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Username"
            name="username"
            type="text"
            placeholder="Enter username"
            value={values.username}
            onChange={onChange}
            status={fieldStatus("username")}
            required
          />
          {errors.username && <FieldError>{errors.username}</FieldError>}
        </div>

        <div>
          <TextInput
            label={marketer ? "New password (optional)" : "Password"}
            name="password"
            type="password"
            placeholder={marketer ? "Leave blank to keep current" : "At least 8 characters"}
            value={values.password}
            onChange={onChange}
            status={fieldStatus("password")}
            required={!marketer}
          />
          {errors.password && <FieldError>{errors.password}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Phone number"
            name="phone_number"
            type="tel"
            placeholder="Enter phone number"
            value={values.phone_number}
            onChange={onChange}
            status={fieldStatus("phone_number")}
            required
          />
          {errors.phone_number && <FieldError>{errors.phone_number}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Referral code"
            name="referral_code"
            type="text"
            placeholder="Enter referral code"
            value={values.referral_code}
            onChange={onChange}
          />
        </div>

        <SelectOption
          items={companyOptions}
          label="Company (optional)"
          placeholder="Select company"
          value={values.company_id}
          onChange={(id) => {
            const company = companies.find((item) => String(item.id) === id);
            setFieldValue("company_id", id);
            setFieldValue("company_name", company?.company_name ?? "");
          }}
        />

        <div>
          <TextInput
            label="Company name"
            name="company_name"
            type="text"
            placeholder="Company name (if not listed)"
            value={values.company_name}
            onChange={(event) => {
              setFieldValue("company_id", "");
              onChange(event);
            }}
            status={fieldStatus("company_name")}
            required
          />
          {errors.company_name && <FieldError>{errors.company_name}</FieldError>}
        </div>

        <div>
          <SelectOption
            items={countryOptions}
            label="Target country"
            placeholder="Select target country"
            value={String(selectedCountryId)}
            onChange={(countryId) => {
              const country = countries.find(
                (item) => String(item.id) === countryId,
              );
              setFieldValue("target_country", country?.name ?? "");
            }}
          />
          {errors.target_country && (
            <FieldError>{errors.target_country}</FieldError>
          )}
        </div>

        <div>
          <TextInput
            label="Payout currency"
            name="payout_currency"
            type="text"
            placeholder="e.g. USD"
            value={values.payout_currency}
            onChange={onChange}
            status={fieldStatus("payout_currency")}
            required
          />
          {errors.payout_currency && <FieldError>{errors.payout_currency}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Experience (years)"
            name="experience_years"
            type="number"
            placeholder="0"
            value={values.experience_years}
            onChange={onChange}
            status={fieldStatus("experience_years")}
            required
          />
          {errors.experience_years && <FieldError>{errors.experience_years}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Regions"
            name="regions"
            type="text"
            placeholder="Separate regions with commas"
            value={values.regions}
            onChange={onChange}
            status={fieldStatus("regions")}
            required
          />
          {errors.regions && <FieldError>{errors.regions}</FieldError>}
        </div>

        <div className="md:col-span-2">
          <TextBox
            label="Sales background"
            name="sales_background"
            placeholder="Describe the marketer's sales background"
            value={values.sales_background}
            onChange={(_, value) => setFieldValue("sales_background", value)}
          />
          {errors.sales_background && <FieldError>{errors.sales_background}</FieldError>}
        </div>

        <div>
          <TextInput
            label="LinkedIn URL"
            name="linkedin_url"
            type="url"
            placeholder="https://linkedin.com/in/..."
            value={values.linkedin_url}
            onChange={onChange}
            status={fieldStatus("linkedin_url")}
            required
          />
          {errors.linkedin_url && <FieldError>{errors.linkedin_url}</FieldError>}
        </div>

        <div>
          <TextInput
            label="BVN"
            name="bvn"
            type="text"
            placeholder="Enter BVN"
            value={values.bvn}
            onChange={onChange}
            status={fieldStatus("identity_number")}
          />
        </div>

        <div>
          <TextInput
            label="NIN"
            name="nin"
            type="text"
            placeholder="Enter NIN"
            value={values.nin}
            onChange={onChange}
            status={fieldStatus("identity_number")}
          />
        </div>

        <div>
          <TextInput
            label="Identity number"
            name="identity_number"
            type="text"
            placeholder="Enter another identity number"
            value={values.identity_number}
            onChange={onChange}
            status={fieldStatus("identity_number")}
          />
          {errors.identity_number && <FieldError>{errors.identity_number}</FieldError>}
        </div>

        <div className="md:col-span-2">
          <Upload
            title="Profile photo"
            label="JPG, PNG"
            name="marketer_profile_photo"
            onChange={onPhotoChange}
            onDelete={onPhotoDelete}
            completed={Boolean(values.profile_photo_path)}
            imgPreview={photoPreview || values.profile_photo_path}
            loading={loading}
            status={fieldStatus("profile_photo_path")}
            required
          />
          {errors.profile_photo_path && (
            <FieldError>{errors.profile_photo_path}</FieldError>
          )}
        </div>

        <div className="md:col-span-2">
          {values.cv_path && (
            <p className="mb-2 text-sm">
              Current CV:{" "}
              <a
                href={cvPreview || values.cv_path}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View file
              </a>
            </p>
          )}
          <DocumentUpload
            title={marketer ? "Replace CV" : "Upload CV"}
            name="marketer_cv"
            label="Upload a PDF, DOC, or DOCX file"
            acceptedFileTypes={[
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ]}
            maxFiles={1}
            onChange={onCvChange}
            onDelete={onCvDelete}
            required={!values.cv_path}
          />
          {errors.cv_path && <FieldError>{errors.cv_path}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Bank code"
            name="bank_code"
            type="text"
            placeholder="Enter bank code"
            value={values.bank_code}
            onChange={onChange}
            status={fieldStatus("bank_code")}
            required
          />
          {errors.bank_code && <FieldError>{errors.bank_code}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Bank name"
            name="bank_name"
            type="text"
            placeholder="Enter bank name"
            value={values.bank_name}
            onChange={onChange}
            status={fieldStatus("bank_name")}
            required
          />
          {errors.bank_name && <FieldError>{errors.bank_name}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Account number"
            name="account_number"
            type="text"
            placeholder="Enter account number"
            value={values.account_number}
            onChange={onChange}
            status={fieldStatus("account_number")}
            required
          />
          {errors.account_number && <FieldError>{errors.account_number}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Account name"
            name="account_name"
            type="text"
            placeholder="Enter account name"
            value={values.account_name}
            onChange={onChange}
            status={fieldStatus("account_name")}
            required
          />
          {errors.account_name && <FieldError>{errors.account_name}</FieldError>}
        </div>

        <div>
          <TextInput
            label="Commission rate (%)"
            name="commission_rate"
            type="number"
            placeholder="0"
            value={values.commission_rate}
            onChange={onChange}
            status={fieldStatus("commission_rate")}
            required
          />
          {errors.commission_rate && <FieldError>{errors.commission_rate}</FieldError>}
        </div>

        <SelectOption
          items={verificationOptions}
          label="Bank verification"
          placeholder="Select verification status"
          value={values.bank_verified}
          onChange={(value) => setFieldValue("bank_verified", value)}
        />

        <SelectOption
          items={statusOptions}
          label="Application status"
          placeholder="Select application status"
          value={values.status}
          onChange={(value) => setFieldValue("status", value)}
        />
      </div>
    </Modal>
  );
};

const FieldError = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-1 text-sm text-red-600" role="alert">
    {children}
  </p>
);

export default MarketerFormModal;
